import { useEffect, useMemo, useRef, useState } from 'react';
import { heroFrames, type HeroKind } from './assets';

interface SpriteProps {
  heroId: string;
  kind: HeroKind;
  size?: number;
  fps?: number;
  /** Loop forever (idle) or play once then call onDone (celebrate). */
  loop?: boolean;
  onDone?: () => void;
}

/** Frame-cycling hero sprite on a plain <img> (no Phaser). */
export function Sprite({ heroId, kind, size = 160, fps = 6, loop = true, onDone }: SpriteProps) {
  const frames = useMemo(() => heroFrames(heroId, kind), [heroId, kind]);
  const [index, setIndex] = useState(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    setIndex(0);
    if (frames.length <= 1) {
      if (!loop) onDoneRef.current?.();
      return;
    }
    let frame = 0;
    const id = setInterval(() => {
      frame += 1;
      if (frame >= frames.length) {
        if (loop) {
          frame = 0;
        } else {
          clearInterval(id);
          setIndex(frames.length - 1);
          onDoneRef.current?.();
          return;
        }
      }
      setIndex(frame);
    }, 1000 / fps);
    return () => clearInterval(id);
  }, [frames, fps, loop]);

  const src = frames[index] ?? frames[0];
  if (!src) return null;
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt=""
      draggable={false}
      style={{ imageRendering: 'pixelated', userSelect: 'none', pointerEvents: 'none' }}
    />
  );
}
